/** מע״מ ברירת מחדל על מנוי/חבילת סוכן (נטו בקטלוג → ברוטו ללקוח ול-HYP). */
export const PACKAGE_SUBSCRIPTION_VAT_RATE = 0.18;

/** תוויות תצוגה סטנדרטיות — SSOT */
export const PACKAGE_SUBSCRIPTION_VAT_INCLUDED_LABEL = 'כולל מע"מ';
export const PACKAGE_SUBSCRIPTION_NET_BEFORE_VAT_LABEL = 'לפני מע"מ';

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

/**
 * SSOT יחיד למחיר מנוי/חבילת סוכן — נטו מהקטלוג + מע״מ.
 * כל תצוגה למשתמש וכל סכום לתשלום חייבים להשתמש ב-grossAmount.
 *
 * @param {unknown} netAmount
 * @param {number} [vatRate=0.18]
 * @returns {{ netAmount: number|null, vatRate: number, vatAmount: number|null, grossAmount: number|null }}
 */
export function resolvePackagePriceWithVat(netAmount, vatRate = PACKAGE_SUBSCRIPTION_VAT_RATE) {
  const rate = Number(vatRate);
  if (netAmount == null || netAmount === "") {
    return {
      netAmount: null,
      vatRate: Number.isFinite(rate) ? rate : PACKAGE_SUBSCRIPTION_VAT_RATE,
      vatAmount: null,
      grossAmount: null,
    };
  }
  const net = Number(netAmount);
  if (!Number.isFinite(net) || net < 0 || !Number.isFinite(rate) || rate < 0) {
    return {
      netAmount: null,
      vatRate: Number.isFinite(rate) ? rate : PACKAGE_SUBSCRIPTION_VAT_RATE,
      vatAmount: null,
      grossAmount: null,
    };
  }

  const netRounded = roundMoney(net);
  const vatAmount = roundMoney(netRounded * rate);
  const grossAmount = roundMoney(netRounded + vatAmount);

  return {
    netAmount: netRounded,
    vatRate: rate,
    vatAmount,
    grossAmount,
  };
}

/**
 * @param {unknown} netAmount
 * @returns {number|null}
 */
export function resolvePackageSubscriptionGrossAmount(netAmount) {
  return resolvePackagePriceWithVat(netAmount).grossAmount;
}

/**
 * תצוגת מחיר ברוטו (כולל מע״מ) למנוי/חבילה.
 * @param {unknown} netAmount
 * @param {{ suffix?: string }} [options]
 */
export function formatPackageSubscriptionGrossPrice(netAmount, options = {}) {
  const { grossAmount } = resolvePackagePriceWithVat(netAmount);
  if (grossAmount == null) return "—";

  const suffix = options.suffix ?? "לחודש";
  const hasFraction = Math.abs(grossAmount % 1) > 1e-9;
  const formatted = hasFraction
    ? grossAmount.toLocaleString("he-IL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : grossAmount.toLocaleString("he-IL", { maximumFractionDigits: 0 });

  return suffix ? `${formatted} ₪ ${suffix}` : `${formatted} ₪`;
}

/**
 * פורמט ILS לתצוגת מחיר מנוי (ברוטו) — לשימוש בדפי ניהול/שדרוג.
 * @param {unknown} netAmount — סכום נטו מהשרת
 */
export function formatPackageSubscriptionGrossIls(netAmount) {
  const { grossAmount } = resolvePackagePriceWithVat(netAmount);
  if (grossAmount == null) return "—";
  try {
    const hasFraction = Math.abs(grossAmount % 1) > 1e-9;
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(grossAmount);
  } catch {
    return `${grossAmount} ₪`;
  }
}

function formatSubscriptionIlsAmount(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  const n = Number(amount);
  const hasFraction = Math.abs(n % 1) > 1e-9;
  try {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(n);
  } catch {
    return `${n} ₪`;
  }
}

/**
 * תצוגת סכום מנוי: ברוטו + «כולל מע״מ», ונטו קטן + «לפני מע״מ».
 * @param {unknown} netAmount — סכום נטו מהשרת/קטלוג
 */
export function resolvePackageSubscriptionChargeDisplay(netAmount) {
  const pricing = resolvePackagePriceWithVat(netAmount);
  if (pricing.grossAmount == null) {
    return {
      pricing,
      grossFormatted: "—",
      netFormatted: "—",
      vatIncludedLabel: PACKAGE_SUBSCRIPTION_VAT_INCLUDED_LABEL,
      netBeforeVatLabel: PACKAGE_SUBSCRIPTION_NET_BEFORE_VAT_LABEL,
    };
  }
  return {
    pricing,
    grossFormatted: formatSubscriptionIlsAmount(pricing.grossAmount),
    netFormatted: formatSubscriptionIlsAmount(pricing.netAmount),
    vatIncludedLabel: PACKAGE_SUBSCRIPTION_VAT_INCLUDED_LABEL,
    netBeforeVatLabel: PACKAGE_SUBSCRIPTION_NET_BEFORE_VAT_LABEL,
  };
}
